import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import EmployeeRegistration from "./components/EmployeeRegistration";
import QRGenerator from "./components/QRGenerator";
import QRScanner from "./components/QRScanner";
import AdminDashboard from "./components/AdminDashboard";
import EmployeeBadges from "./components/EmployeeBadges";
import EmployeePortal from "./components/EmployeePortal";
import Home from "./components/Home";
import AdminLogin from "./components/AdminLogin";
import "./styles.css";

import { getEmployees, createEmployee } from "./api/employeesApi";
import { getAttendance, createAttendance } from "./api/attendanceApi";

const PrivateRoute = ({ children, isAdminLoggedIn }) => {
  if (!isAdminLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AppContent = () => {
  const [employees, setEmployees] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [currentDate, setCurrentDate] = useState("");
  const [isDesktop, setIsDesktop] = useState(true);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [dataError, setDataError] = useState("");

  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      if (window.innerWidth >= 1024) {
        document.body.classList.add("desktop-mode");
      } else {
        document.body.classList.remove("desktop-mode");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    const today = new Date();
    setCurrentDate(today.toISOString().split("T")[0]);

    const loggedIn = localStorage.getItem("isAdminLoggedIn") === "true";
    setIsAdminLoggedIn(loggedIn);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch data (We will move this to React Query in the future)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setDataError("");
        const [emp, att] = await Promise.all([getEmployees(), getAttendance()]);
        if (cancelled) return;
        setEmployees(Array.isArray(emp) ? emp : []);
        setAttendanceRecords(Array.isArray(att) ? att : []);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setDataError(
            "No se pudo conectar al servidor de MySQL (API)."
          );
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("isAdminLoggedIn", isAdminLoggedIn);
  }, [isAdminLoggedIn]);

  const handleEmployeeAdded = (employee) => {
    const run = async () => {
      try {
        setDataError("");
        const created = await createEmployee(employee);
        setEmployees((prev) => [created, ...prev]);
        setCurrentEmployee(created);
      } catch (err) {
        console.error(err);
        setDataError(err?.message || "No se pudo registrar el empleado.");
      }
    };
    run();
  };

  const handleScan = (employee, location = null) => {
    const now = new Date();
    const today = now.toISOString().split("T")[0];

    const lastRecordForToday = attendanceRecords
      .find((r) => r.dni === employee.dni && r.date === today);

    const nextType =
      lastRecordForToday?.type === "entrada" ? "salida" : "entrada";

    const record = {
      id: `att-${employee.dni}-${now.getTime()}`,
      employeeId: employee.id,
      employeeName: `${employee.nombre} ${employee.apellidos}`,
      dni: employee.dni,
      date: today,
      time: now.toLocaleTimeString(),
      type: nextType,
      ...(location ? { latitude: String(location.latitude), longitude: String(location.longitude) } : {})
    };

    const run = async () => {
      try {
        setDataError("");
        const created = await createAttendance(record);
        setAttendanceRecords((prev) => [created, ...prev]);
      } catch (err) {
        console.error(err);
        setDataError(err?.message || "No se pudo guardar la asistencia.");
      }
    };
    run();
  };

  const isPublicRoute = location.pathname === "/login" || location.pathname === "/portal";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {isAdminLoggedIn && !isPublicRoute && (
        <Navbar
          isDesktop={isDesktop}
          isAdminLoggedIn={isAdminLoggedIn}
          setIsAdminLoggedIn={setIsAdminLoggedIn}
        />
      )}
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {dataError && (
          <div className="max-w-4xl mx-auto mb-6 p-4 glass-card border-red-500/30 text-red-400 rounded-xl flex items-center gap-3 animate-fade-in-up">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>{dataError}</span>
          </div>
        )}
        
        <Routes>
          <Route path="/login" element={
            !isAdminLoggedIn ? (
              <AdminLogin onLoginSuccess={() => setIsAdminLoggedIn(true)} />
            ) : (
              <Navigate to="/" replace />
            )
          } />
          
          <Route path="/portal" element={
            <div className="animate-fade-in-up">
              <EmployeePortal 
                employees={employees} 
                attendanceRecords={attendanceRecords} 
              />
            </div>
          } />

          {/* Rutas Privadas Admin */}
          <Route path="/" element={
            <PrivateRoute isAdminLoggedIn={isAdminLoggedIn}>
              <Home isDesktop={isDesktop} />
            </PrivateRoute>
          } />
          
          <Route path="/registro" element={
            <PrivateRoute isAdminLoggedIn={isAdminLoggedIn}>
              <div className="flex flex-col lg:flex-row gap-8 max-w-5xl mx-auto items-start justify-center animate-fade-in-up">
                <div className="flex-1 w-full">
                  <EmployeeRegistration
                    onEmployeeAdded={handleEmployeeAdded}
                    currentDate={currentDate}
                  />
                </div>
                {currentEmployee && (
                  <div className="flex-1 w-full animate-fade-in-up">
                    <QRGenerator employee={currentEmployee} />
                  </div>
                )}
              </div>
            </PrivateRoute>
          } />
          
          <Route path="/scanner" element={
            <PrivateRoute isAdminLoggedIn={isAdminLoggedIn}>
              <div className="animate-fade-in-up">
                <QRScanner
                  onScan={handleScan}
                  employees={employees}
                  isDesktop={isDesktop}
                />
              </div>
            </PrivateRoute>
          } />
          
          <Route path="/admin" element={
            <PrivateRoute isAdminLoggedIn={isAdminLoggedIn}>
              <div className="animate-fade-in-up">
                <AdminDashboard
                  employees={employees}
                  attendanceRecords={attendanceRecords}
                />
              </div>
            </PrivateRoute>
          } />
          
          <Route path="/gafetes" element={
            <PrivateRoute isAdminLoggedIn={isAdminLoggedIn}>
              <div className="animate-fade-in-up">
                <EmployeeBadges employees={employees} />
              </div>
            </PrivateRoute>
          } />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const App = () => (
  <BrowserRouter>
    <AppContent />
  </BrowserRouter>
);

export default App;
